import { useEffect, useRef, useState } from "react";
import GoogleMapReact from "google-map-react";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { Button, Card, Icon, Spinner, cn } from "@playerelite/ui";
import {
  AUSTRALIA_VIEW,
  collectFenceCoordinates,
  fitMapToCoordinates,
  hasUsableCoordinates,
} from "../../utils/map-view";
import { MapFence } from "./geo-analytics-utils";

type Props = {
  fences: MapFence[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  selected: string | null;
  /** Called with the venue to select, or null to clear the selection. */
  onSelect: (name: string | null) => void;
};

/** A kit colour token as a colour Google Maps accepts, which rules out `rgb(r g b)`. */
const tokenColour = (name: string, fallback: string) => {
  const channels = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return channels ? `rgb(${channels.split(/\s+/).join(", ")})` : fallback;
};

const polygonStyle = (isSelected: boolean) => {
  const colour = isSelected
    ? tokenColour("--pe-warning", "#d97706")
    : tokenColour("--pe-action", "#1568b8");
  return {
    strokeColor: colour,
    strokeOpacity: 0.9,
    strokeWeight: isSelected ? 3 : 2,
    fillColor: colour,
    fillOpacity: isSelected ? 0.35 : 0.2,
    zIndex: isSelected ? 2 : 1,
  };
};

type MarkerProps = {
  // Read by google-map-react to position the child; unused by the marker itself.
  lat: number;
  lng: number;
  name: string;
  isSelected: boolean;
  onClick: () => void;
};

const FenceMarker = ({ name, isSelected, onClick }: MarkerProps) => (
  <button
    type="button"
    title={name}
    aria-label={`Select ${name}`}
    aria-pressed={isSelected}
    onClick={onClick}
    className={cn(
      "absolute inline-flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white shadow-lg transition-transform hover:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
      isSelected
        ? "z-10 h-9 w-9 bg-warning text-warning-fg"
        : "h-7 w-7 bg-action text-action-fg"
    )}
  >
    <PlaceOutlinedIcon sx={{ fontSize: isSelected ? 20 : 16 }} />
  </button>
);

const VenueMap = ({
  fences,
  selected,
  onSelect,
}: Pick<Props, "fences" | "selected" | "onSelect">) => {
  const [map, setMap] = useState<any>(null);
  // Keyed by locationId: two fences can share a name, and a name key would drop one of them
  // from cleanup and leave its outline on the map.
  const polygons = useRef<Map<string, { name: string; polygon: any }>>(new Map());

  // The polygon listeners are bound once per draw, so they read the latest handler from a ref.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!map) return;
    const drawn = new Map<string, { name: string; polygon: any }>();
    fences.filter(hasUsableCoordinates).forEach((fence) => {
      const polygon = new google.maps.Polygon({
        paths: fence.coordinates,
        clickable: true,
        ...polygonStyle(false),
      });
      polygon.addListener("click", () => onSelectRef.current(fence.name));
      polygon.setMap(map);
      drawn.set(fence.locationId, { name: fence.name, polygon });
    });
    polygons.current = drawn;
    return () => drawn.forEach(({ polygon }) => polygon.setMap(null));
  }, [map, fences]);

  useEffect(() => {
    polygons.current.forEach(({ name, polygon }) =>
      polygon.setOptions(polygonStyle(name === selected))
    );
  }, [selected, map, fences]);

  // Frame the map around the fences this venue monitors; re-runs when the venue changes.
  useEffect(() => {
    fitMapToCoordinates(map, collectFenceCoordinates(fences));
  }, [map, fences]);

  return (
    <GoogleMapReact
      bootstrapURLKeys={{
        // TODO: move the Google Maps key to REACT_APP_GOOGLE_MAPS_API_KEY, rotate it,
        // and restrict it to the deployed origins. Deferred: not actionable yet.
        key: "AIzaSyC2M5Q4tBg_vWDT1rsw59Jse5E5PDwXnlA",
      }}
      // Only the pre-data view; the effect above refits once fences load.
      defaultCenter={AUSTRALIA_VIEW.center}
      defaultZoom={AUSTRALIA_VIEW.zoom}
      yesIWantToUseGoogleMapApiInternals
      onGoogleApiLoaded={({ map }) => setMap(map)}
    >
      {fences
        .filter((fence) => fence.centre)
        .map((fence) => (
          <FenceMarker
            key={fence.locationId}
            lat={fence.centre!.lat}
            lng={fence.centre!.lng}
            name={fence.name}
            isSelected={fence.name === selected}
            onClick={() => onSelect(fence.name === selected ? null : fence.name)}
          />
        ))}
    </GoogleMapReact>
  );
};

/** The venue chips, the selected-venue pill and the map, sharing one selection. */
const VenueMapCard = ({ fences, isLoading, isError, onRetry, selected, onSelect }: Props) => (
  <Card padding="none" className="overflow-hidden">
    <div className="flex flex-col gap-3 border-b border-border p-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-base font-semibold">
            Monitoring {fences.length} {fences.length === 1 ? "geofence" : "geofences"}
          </h2>
          <p className="text-xs text-fg-muted">
            Select a venue or map marker to trace its activity through every tier.
          </p>
        </div>
        {selected ? (
          <button
            type="button"
            onClick={() => onSelect(null)}
            aria-label={`Clear selected venue ${selected}`}
            className="inline-flex h-8 items-center gap-1.5 self-start rounded-md bg-action/10 px-3 text-sm font-medium text-fg transition-colors hover:bg-action/20"
          >
            <PlaceOutlinedIcon sx={{ fontSize: 16 }} className="text-action" />
            {selected}
            <Icon name="x" size="sm" className="text-fg-muted" />
          </button>
        ) : null}
      </div>

      {fences.length > 0 ? (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Venues">
          {fences.map((fence) => {
            const isSelected = fence.name === selected;
            return (
              <button
                key={fence.locationId}
                type="button"
                aria-pressed={isSelected}
                onClick={() => onSelect(isSelected ? null : fence.name)}
                className={cn(
                  "inline-flex h-7 items-center whitespace-nowrap rounded-full border px-3 text-xs font-medium transition-colors",
                  isSelected
                    ? "border-action bg-action text-action-fg hover:bg-action-hover"
                    : "border-border bg-surface text-fg hover:bg-action/10"
                )}
              >
                {fence.name}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>

    <div className="relative h-[420px] bg-surface-sunken">
      <VenueMap fences={fences} selected={selected} onSelect={onSelect} />
      {isLoading || isError ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-surface/70 text-sm text-fg-muted">
          {isLoading ? (
            <Spinner size="md" label="Loading geofences" />
          ) : (
            <>
              <span>Couldn't load this venue's geofences.</span>
              <Button variant="secondary" size="sm" onClick={onRetry}>
                Try again
              </Button>
            </>
          )}
        </div>
      ) : null}
    </div>
  </Card>
);

export default VenueMapCard;
